import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import './styles/global.css';

// Initialize Sentry (no-ops if DSN is not set)
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_STAGE || 'staging',
    tracesSampleRate: process.env.REACT_APP_STAGE === 'production' ? 0.2 : 1.0,
  });
}

// Initialize Google Analytics 4 (no-ops if measurement ID is not set).
// Page views are sent manually via usePageTracking on SPA route changes
// (send_page_view: false). Privacy: Google Signals and ad personalization
// are disabled; data redaction and 14-month retention are configured in
// the GA4 admin console.
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
if (GA_MEASUREMENT_ID) {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  const w = window as unknown as {
    dataLayer: IArguments[];
    gtag: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  // IMPORTANT: Push `arguments` (not a rest-param Array). GA4's Consent Mode
  // detects consent commands by checking for the IArguments object identity;
  // a plain Array push is treated as a generic dataLayer event, leaving the
  // consent state inactive and silently dropping all collect requests.
  // eslint-disable-next-line
  w.gtag = function () { w.dataLayer.push(arguments); };

  w.gtag('js', new Date());
  // Explicit consent default required to activate GA4's consent state machine.
  // Posture: no banner, US-only audience, granted by default, disclosed in
  // privacy policy with opt-out instructions.
  w.gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
  w.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
            <App />
          </Sentry.ErrorBoundary>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);